// Calendar view of the Tasks list: tasks sit on their due date. Month or week;
// clicking a day's empty space opens a new task already dated that day.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  addPlainDays,
  easternToday,
  formatPlainDate,
  intlLocale,
  type PlainDate,
} from '../../lib/datetime'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { listTasks, staffTaskKeys, type StaffTask, type TaskListParams } from './api'
import { monthGrid, shiftMonth, weekGrid } from './dates'
import { taskDetailRoute, type TaskShell } from './lib'
import { TaskFormDialog } from './TaskFormDialog'

const MONTH_CELL_LIMIT = 3

type Props = {
  shell: TaskShell
  /** The list's own filters (scope, status, assignee, search); dates come from the grid. */
  filters: Omit<TaskListParams, 'due_from' | 'due_to' | 'limit' | 'offset'>
}

export function TaskCalendar({ shell, filters }: Props) {
  const t = useT(staffTasksI18n)
  const today = easternToday()
  const [mode, setMode] = useState<'month' | 'week'>('month')
  const [month, setMonth] = useState(today.slice(0, 7))
  const [weekOf, setWeekOf] = useState<PlainDate>(today)
  const [newOn, setNewOn] = useState<PlainDate | null>(null)

  const days = useMemo(
    () => (mode === 'month' ? monthGrid(month) : weekGrid(weekOf)),
    [mode, month, weekOf],
  )
  const params: TaskListParams = {
    ...filters,
    due_from: days[0],
    due_to: days[days.length - 1],
    limit: 200,
  }
  const tasks = useQuery({ queryKey: staffTaskKeys.list(params), queryFn: () => listTasks(params) })

  const byDay = useMemo(() => {
    const map = new Map<string, StaffTask[]>()
    for (const task of tasks.data?.items ?? []) {
      if (!task.due_date) continue
      map.set(task.due_date, [...(map.get(task.due_date) ?? []), task])
    }
    return map
  }, [tasks.data])

  const heading = useMemo(() => {
    const [y, m] = (mode === 'month' ? month : weekOf.slice(0, 7)).split('-').map(Number)
    return new Intl.DateTimeFormat(intlLocale(), {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, 1)))
  }, [mode, month, weekOf])

  const step = (amount: number) => {
    if (mode === 'month') setMonth((current) => shiftMonth(current, amount))
    else setWeekOf((current) => addPlainDays(current, amount * 7))
  }

  return (
    <section
      data-testid="staff-task-calendar"
      className="rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-3 shadow-[var(--surface-lifted-shadow)] sm:p-4"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" aria-label={t.calendar.prev} onClick={() => step(-1)}>
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h2 className="m-0 min-w-[9rem] text-center text-sm font-semibold" aria-live="polite">
          {heading}
        </h2>
        <Button variant="ghost" size="icon" aria-label={t.calendar.next} onClick={() => step(1)}>
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setMonth(today.slice(0, 7))
            setWeekOf(today)
          }}
        >
          {t.views.today}
        </Button>
        <div className="ml-auto flex gap-1" role="tablist">
          {(['month', 'week'] as const).map((value) => (
            <Button
              key={value}
              role="tab"
              aria-selected={mode === value}
              size="sm"
              variant={mode === value ? 'default' : 'outline'}
              onClick={() => setMode(value)}
            >
              {t.views[value]}
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="grid min-w-[42rem] grid-cols-7 gap-1">
          {t.weekdays.map((name) => (
            <div key={name} className="px-1 pb-1 text-xs font-medium text-[var(--color-text-muted)]">
              {name}
            </div>
          ))}
          {days.map((day) => {
            const items = byDay.get(day) ?? []
            const inMonth = mode === 'week' || day.startsWith(month)
            const shown = mode === 'month' ? items.slice(0, MONTH_CELL_LIMIT) : items
            return (
              <div
                key={day}
                data-testid={`calendar-day-${day}`}
                className={cn(
                  'group flex flex-col gap-1 rounded-md border border-[var(--color-border)] p-1.5',
                  mode === 'month' ? 'min-h-[6.5rem]' : 'min-h-[16rem]',
                  !inMonth && 'bg-[var(--color-surface-muted)] opacity-70',
                  day === today && 'border-[var(--color-info)]',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      day === today ? 'font-semibold text-[var(--color-info)]' : 'text-[var(--color-text-muted)]',
                    )}
                  >
                    {Number(day.slice(8))}
                  </span>
                  <button
                    type="button"
                    aria-label={t.calendar.addOn(formatPlainDate(day))}
                    onClick={() => setNewOn(day)}
                    className="rounded p-0.5 text-[var(--color-text-muted)] opacity-0 hover:bg-[var(--color-surface-muted)] focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
                {shown.map((task) => (
                  <Link
                    key={task.id}
                    {...taskDetailRoute(shell, task.id)}
                    title={task.title}
                    className={cn(
                      'truncate rounded px-1.5 py-0.5 text-xs',
                      task.status === 'done'
                        ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] line-through'
                        : task.is_overdue
                          ? 'bg-[color-mix(in_srgb,var(--color-danger)_12%,var(--color-surface))] text-[var(--color-danger)]'
                          : 'bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-text)]',
                    )}
                  >
                    {task.title}
                  </Link>
                ))}
                {items.length > shown.length && (
                  <span className="px-1 text-xs text-[var(--color-text-muted)]">
                    {t.calendar.more(items.length - shown.length)}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <TaskFormDialog
        open={newOn !== null}
        onOpenChange={(open) => !open && setNewOn(null)}
        initialDueDate={newOn ?? undefined}
      />
    </section>
  )
}
